import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { SearchMd } from '@kula-ai/icons/outline/SearchMd';
import { CornerDownLeft } from '@kula-ai/icons/dual-tone/CornerDownLeft';
import { XClose } from '@kula-ai/icons/outline/XClose';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { KText } from '@/ui-library/components/text';

const DEFAULT_PLACEHOLDERS = [
  'Show me engineers from Series A startups with Python and machine learning experience',
  'Looking for candidates we rejected because of salary expectations in the last 6months',
  'Show me all my open jobs in engineering department',
  'Show me all interviews without scorecards filled',
  'Find candidates who declined our offers in the last 12 months',
];

const slideUp = keyframes`
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(-100%); opacity: 0; }
`;

const slideIn = keyframes`
  0% { transform: translateY(100%); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

interface SearchInputProps {
  onType?: (_value: string) => void;
  onInputEnter?: (_value: string) => void;
  onClear?: () => void;
  onClose?: () => void;
  onCancelSearch?: () => void;
  isLoading?: boolean;
  debounceTime?: number;
  placeholders?: string[];
  placeholderInterval?: number;
  externalSearchTerm?: { query: string; timestamp: number } | null;
}

export const SearchInput = ({
  onType,
  onInputEnter,
  onClear,
  onClose,
  onCancelSearch,
  isLoading = false,
  debounceTime = 200,
  placeholders = DEFAULT_PLACEHOLDERS,
  placeholderInterval = 3000,
  externalSearchTerm,
}: SearchInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedOnType = useMemo(() => {
    if (!onType) return null;
    return debounce((value: string) => {
      onType(value);
    }, debounceTime);
  }, [onType, debounceTime]);

  useEffect(() => {
    return () => {
      debouncedOnType?.cancel();
    };
  }, [debouncedOnType]);

  useEffect(() => {
    if (placeholders.length <= 1) return;

    const interval = setInterval(() => {
      if (isFirstRender) {
        setIsFirstRender(false);
      }
      setIsAnimating(true);

      setTimeout(() => {
        setCurrentPlaceholder((prev) => {
          let next = Math.floor(Math.random() * placeholders.length);
          while (next === prev && placeholders.length > 1) {
            next = Math.floor(Math.random() * placeholders.length);
          }
          return next;
        });
        setIsAnimating(false);
      }, 300);
    }, placeholderInterval);

    return () => clearInterval(interval);
  }, [placeholders, placeholderInterval, isFirstRender]);

  useEffect(() => {
    if (externalSearchTerm?.query) {
      setTimeout(() => {
        setIsSubmitted(true);
        setSubmittedSearch(externalSearchTerm.query);
        setIsEditing(false);
        setInputValue('');
        onInputEnter?.(externalSearchTerm.query);
      }, 100);
    }
  }, [externalSearchTerm]); //eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (!value) {
      setIsSubmitted(false);
      setIsEditing(false);
      debouncedOnType?.cancel();
      onClear?.();
    } else {
      debouncedOnType?.(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue) {
      debouncedOnType?.cancel();
      setIsSubmitted(true);
      setSubmittedSearch(inputValue);
      setIsEditing(false);
      onInputEnter?.(inputValue);
      setInputValue('');
    }
  };

  const handleClear = () => {
    setInputValue('');
    setIsSubmitted(false);
    setIsEditing(false);
    setSubmittedSearch('');
    debouncedOnType?.cancel();
    onClear?.();
    inputRef.current?.focus();
  };

  const handleEdit = () => {
    if (isLoading) onCancelSearch?.();
    setInputValue(submittedSearch);
    setIsEditing(true);
    setIsSubmitted(false);
    inputRef.current?.focus();
  };

  return (
    <>
      <Box
        borderRadius="13.5px"
        p="2.25px"
        background={
          'linear-gradient(269.78deg, #8A90FF 0%, #F6EFE4 31.73%, #B15FDA 100%)'
        }
        transition="background 0.3s ease"
      >
        <Flex
          direction="column"
          border={'none'}
          borderColor="secondary-200"
          borderRadius="12px"
          bg="white"
          position="relative"
        >
          <InputGroup
            height="58px"
            border="none"
            _hover={{ border: 'none' }}
            _focus={{ border: 'none' }}
            _focusVisible={{ border: 'none' }}
            alignItems="center"
          >
            <InputLeftElement height="100%" pointerEvents="none" pl="10px">
              <Icon
                as={SearchMd}
                color="secondary-500"
                width="20px"
                height="20px"
              />
            </InputLeftElement>

            <Input
              _hover={{ border: 'none' }}
              _focus={{ border: 'none' }}
              _focusVisible={{ border: 'none' }}
              _active={{ border: 'none' }}
              ref={inputRef}
              value={inputValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              height="48px"
              bg="white"
              color="secondary-900"
              fontSize="14px"
              pl="44px"
              pr="200px"
              borderRadius="10px"
              sx={{
                border: 'none',
                _placeholder: { color: 'secondary-500' },
                _focus: { boxShadow: 'none' },
              }}
            />

            {!inputValue && (
              <Box
                position="absolute"
                left="44px"
                top="0"
                height="100%"
                display="flex"
                alignItems="center"
                pointerEvents="none"
                overflow="hidden"
                zIndex={1}
              >
                <KText
                  size="sm-regular"
                  color="secondary-500"
                  whiteSpace="nowrap"
                  animation={
                    isFirstRender
                      ? 'none'
                      : isAnimating
                        ? `${slideUp} 0.3s ease-in forwards`
                        : `${slideIn} 0.3s ease-out forwards`
                  }
                >
                  {placeholders[currentPlaceholder]}
                </KText>
              </Box>
            )}

            <InputRightElement height="100%" width="auto" pr="16px">
              {isSubmitted || submittedSearch ? (
                <Flex
                  as="button"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="8px"
                  w="32px"
                  h="32px"
                  cursor="pointer"
                  onClick={() => {
                    handleClear();
                    onClose?.();
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'secondary-100',
                    _hover: { bg: 'secondary-50' },
                  }}
                >
                  <Icon
                    as={XClose}
                    color="secondary-600"
                    width="18px"
                    height="18px"
                  />
                </Flex>
              ) : (
                <Flex
                  alignItems="center"
                  gap="6px"
                  className="ai-search-input-right-element-press-enter"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="4px"
                    p="2px 4px"
                  >
                    <Icon
                      as={CornerDownLeft}
                      color="secondary-500"
                      width="14px"
                      height="14px"
                    />
                  </Flex>
                  <Text
                    fontSize="12px"
                    color="secondary-500"
                    whiteSpace="nowrap"
                    userSelect="none"
                  >
                    Press Enter to search
                  </Text>
                </Flex>
              )}
            </InputRightElement>
          </InputGroup>
          {submittedSearch && !isEditing && (
            <Flex
              className="ai-search-input-submitted-search"
              alignItems="center"
              gap="6px"
              pb="10px"
              px="16px"
            >
              <KText
                size="sm-medium"
                bg="secondary-50"
                p="2px 4px"
                borderRadius="4px"
                color="secondary-700"
                lineHeight={'18px'}
              >
                current
              </KText>
              <KText
                size="sm-regular"
                color="secondary-700"
                noOfLines={1}
                maxW="500px"
                title={submittedSearch}
              >
                {submittedSearch}
              </KText>
              <Flex
                as="button"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                flexShrink={0}
                onClick={handleEdit}
                ml="4px"
              >
                <Icon
                  as={Edit05}
                  color="secondary-700"
                  width="16px"
                  height="16px"
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Box>
    </>
  );
};
